<template>
  <div class="vip-page">
    <PromoSkeleton v-if="isPending" variant="vip" />

    <p v-else-if="isError" class="vip-state">
      {{ t('promo.vip.loadError') }}

      <button type="button" class="retry-btn" @click="() => refetch()">
        {{ t('promo.vip.retry') }}
      </button>
    </p>

    <template v-else>
      <VipLevelHero :status="vipStatus" :percent="progressPercent" @rules="onRules" />

      <div class="reward-grid">
        <VipRewardCard
          v-for="(card, idx) in rewardCards"
          :key="card.kind"
          :style="{ '--i': idx }"
          :card="card"
          @claim="onClaim(card)"
          @refresh="() => refetch()"
        />
      </div>

      <VipBenefitTable
        :rows="vipLevelBenefits"
        :total-levels="vipTotalLevels"
        :current-level="vipStatus.currentLevel"
      />
    </template>

    <Transition name="toast-fade">
      <div v-if="toastMessage" class="vip-toast">{{ toastMessage }}</div>
    </Transition>

    <VipRulesDrawer v-model="rulesOpen" @close="rulesOpen = false" />
  </div>
</template>

<script setup lang="ts">
import { claimPeriodicSalary, claimUpgradeBonus } from '~/api/promo'

import type { PeriodicCycleType, VipClaimStatus } from '~/types/api/promo'

import { formatBaseAmt } from '~/utils/transfer/money'

import { useVipSalaryPanel } from './composables/useVipSalaryPanel'

import type { VipRewardCard as VipRewardCardType, VipRewardKind } from './types'

import PromoSkeleton from '../components/PromoSkeleton.vue'

import VipBenefitTable from './components/VipBenefitTable.vue'

import VipLevelHero from './components/VipLevelHero.vue'

import VipRewardCard from './components/VipRewardCard.vue'

import VipRulesDrawer from './components/VipRulesDrawer.vue'

const { t } = useI18n()
const { load: loadOverview } = useRewardOverview()

const { data, isPending, isError, refetch } = useVipSalaryPanel()

const rewardCards = ref<VipRewardCardType[]>([])

watch(
  data,

  val => {
    if (val) rewardCards.value = val.rewardCards.map(card => ({ ...card }))
  },

  { immediate: true },
)

const vipStatus = computed(
  () => data.value?.status ?? { currentLevel: 0, nextLevel: 1, amountToNext: 0 },
)

const progressPercent = computed(() => data.value?.progressPercent ?? 0)

const vipLevelBenefits = computed(() => data.value?.levelBenefits ?? [])

const vipTotalLevels = computed(() => data.value?.totalLevels ?? 0)

const toastMessage = ref('')

let toastTimer: ReturnType<typeof setTimeout> | null = null

const showToast = (message: string) => {
  if (toastTimer) clearTimeout(toastTimer)

  toastMessage.value = message

  toastTimer = setTimeout(() => {
    toastMessage.value = ''
  }, 2000)
}

const rulesOpen = ref(false)

const onRules = () => {
  rulesOpen.value = true
}

const userStore = useUserStore()

const CYCLE_TYPE_BY_KIND: Partial<Record<VipRewardKind, PeriodicCycleType>> = {
  daily: 'DAILY',

  weekly: 'WEEKLY',

  monthly: 'MONTHLY',
}

/**
 * 领取结果落到卡片：CLAIMED → 已领取 ✓；PROCESSING（钱包还没落定）→ 发放中，
 * 与面板返回 state=PROCESSING 的展示一致（按钮置灰，不再可点），下次拉面板以服务端为准。
 */
const applyClaimResult = (card: VipRewardCardType, status: VipClaimStatus, amount: number) => {
  const processing = status === 'PROCESSING'
  card.state = processing ? 'PROCESSING' : 'CLAIMED'
  card.claimed = !processing
  card.actionText = t(processing ? 'promo.vip.action.PROCESSING' : 'promo.vip.action.CLAIMED')
  showToast(
    t(processing ? 'promo.vip.claimProcessing' : 'promo.vip.claimReceived', {
      label: card.label,
      amount: formatBaseAmt(amount, userStore.baseCurrency),
    }),
  )
}

const onClaim = async (card: VipRewardCardType) => {
  if (card.kind === 'upgrade') {
    card.claimable = false // 领取中禁止重复点击

    try {
      const { results } = await claimUpgradeBonus()

      const claimed = results.find(item => item.type === 'UPGRADE')

      applyClaimResult(card, claimed?.status ?? 'CLAIMED', Number(claimed?.amount) || card.amount)
      void loadOverview() // 领取后刷新页签角标
    } catch (error) {
      card.claimable = true // 失败回滚，允许重试；业务错误已由 api 拦截器 toast

      console.error('Claim upgrade bonus failed:', error)
    }

    return
  }

  const cycleType = CYCLE_TYPE_BY_KIND[card.kind]!
  card.claimable = false // 领取中禁止重复点击
  try {
    const result = await claimPeriodicSalary({ cycleNo: card.cycleNo!, cycleType })
    applyClaimResult(card, result.status, Number(result.amount) || card.amount)
    void loadOverview() // 领取后刷新页签角标
  } catch (error) {
    card.claimable = true // 失败回滚，允许重试；业务错误已由 api 拦截器 toast
    console.error('Claim periodic salary failed:', error)
  }
}
</script>

<style scoped lang="scss">
.vip-page {
  margin-top: 12px;

  padding: 0 14px;
}

.vip-state {
  display: flex;

  align-items: center;

  gap: 10px;

  justify-content: center;

  padding: 60px 0;

  font-size: 13px;

  color: var(--muted);
}

.retry-btn {
  padding: 4px 12px;

  border-radius: var(--r-pill);

  background: var(--primary);

  color: #fff;

  font-size: 12px;

  font-weight: 700;
}

.reward-grid {
  display: grid;

  grid-template-columns: 1fr 1fr;

  gap: 10px;

  margin: 14px 0 4px;
}

.vip-toast {
  position: fixed;

  left: 50%;

  bottom: calc(var(--tabbar-h) + 24px + env(safe-area-inset-bottom, 0px));

  transform: translateX(-50%);

  z-index: 80;

  padding: 10px 18px;

  border-radius: var(--r-pill);

  background: rgba(20, 18, 30, 0.92);

  color: #fff;

  font-size: 13px;

  font-weight: 700;

  box-shadow: var(--shadow);

  white-space: nowrap;
}

.toast-fade-enter-active,
.toast-fade-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.toast-fade-enter-from,
.toast-fade-leave-to {
  opacity: 0;

  transform: translateX(-50%) translateY(6px);
}
</style>
